import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import SafeIcon from '../common/SafeIcon';
import KeyPhrasesInput from './KeyPhrasesInput';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave, FiExternalLink, FiTrendingUp, FiTarget, FiFileText, FiSearch, FiEdit3, FiShare2, FiImage, FiMessageCircle, FiInstagram, FiFacebook, FiLinkedin, FiCheckCircle } = FiIcons;

const PageDetail = () => {
  const navigate = useNavigate();
  const { websiteId, pageId } = useParams();
  const { websites, updatePage } = useData();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('seo');

  const website = websites.find(w => w.id === websiteId);
  const page = website?.pages?.find(p => p.id === pageId);

  const [formData, setFormData] = useState({
    title: page?.title || '',
    url: page?.url || '',
    content: page?.content || '',
    keyPhrases: page?.keyPhrases?.map(kp => kp.phrase) || [],
    meta_description: page?.meta_description || '',
    ranking: page?.ranking || '',
    social_media_caption: page?.social_media_caption || '',
    social_media_image_url: page?.social_media_image_url || '',
    social_media_platforms: page?.social_media_platforms || ['Facebook', 'Instagram', 'LinkedIn'],
    social_media_created: page?.social_media_created || false,
    social_media_notes: page?.social_media_notes || ''
  });

  if (!website || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Page not found</h2>
          <button
            onClick={() => navigate('/admin')}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updatePage(websiteId, pageId, formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating page:', error);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: page.title,
      url: page.url,
      content: page.content,
      keyPhrases: page.keyPhrases?.map(kp => kp.phrase) || [],
      meta_description: page.meta_description || '',
      ranking: page.ranking || '',
      social_media_caption: page.social_media_caption || '',
      social_media_image_url: page.social_media_image_url || '',
      social_media_platforms: page.social_media_platforms || ['Facebook', 'Instagram', 'LinkedIn'],
      social_media_created: page.social_media_created || false,
      social_media_notes: page.social_media_notes || ''
    });
    setIsEditing(false);
  };

  const togglePlatform = (platform) => {
    setFormData(prev => ({
      ...prev,
      social_media_platforms: prev.social_media_platforms.includes(platform)
        ? prev.social_media_platforms.filter(p => p !== platform)
        : [...prev.social_media_platforms, platform]
    }));
  };

  const generateSocialCaption = () => {
    const keywords = page.keyPhrases?.map(kp => kp.phrase).join(', ') || '';
    const caption = `🚀 Discover ${page.title}!

${page.content}

Perfect for: ${keywords}

👉 Learn more: ${page.url}

#SEO #DigitalMarketing #Growth ${keywords.split(', ').map(k => '#' + k.trim().replace(/\s+/g, '')).join(' ')}`;
    
    setFormData(prev => ({
      ...prev,
      social_media_caption: caption
    }));
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'Facebook': return FiFacebook;
      case 'Instagram': return FiInstagram;
      case 'LinkedIn': return FiLinkedin;
      default: return FiShare2;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Enhanced Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => navigate(`/admin/website/${websiteId}`)}
              className="p-3 text-gray-600 hover:bg-gray-200 rounded-xl transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                {page.title}
              </h1>
              <p className="text-gray-600 text-lg mt-1">{website.name}</p>
            </div>
          </div>
          <div className="flex space-x-4">
            <a
              href={page.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-6 py-3 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors border border-blue-200"
            >
              <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
              <span className="font-medium">View Live Page</span>
            </a>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-3 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg flex items-center space-x-2"
              >
                <SafeIcon icon={FiEdit3} className="w-5 h-5" />
                <span>Edit Page</span>
              </button>
            ) : (
              <div className="flex space-x-3">
                <button
                  onClick={handleSubmit}
                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-8 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg flex items-center space-x-2"
                >
                  <SafeIcon icon={FiSave} className="w-5 h-5" />
                  <span>Save Changes</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-100 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-white rounded-xl p-2 shadow-lg border border-gray-200">
          <button
            onClick={() => setActiveTab('seo')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
              activeTab === 'seo'
                ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiSearch} className="w-5 h-5" />
            <span>SEO Content</span>
          </button>
          <button
            onClick={() => setActiveTab('social')}
            className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
              activeTab === 'social'
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <SafeIcon icon={FiShare2} className="w-5 h-5" />
            <span>Social Media</span>
            {page.social_media_created && (
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {activeTab === 'seo' && (
              <>
                {/* SEO Information Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiFileText} className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">SEO Information</h2>
                  </div>

                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Page Title
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Page URL
                        </label>
                        <input
                          type="url"
                          value={formData.url}
                          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Page Content & Description
                        </label>
                        <textarea
                          value={formData.content}
                          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 h-32"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Meta Description
                        </label>
                        <textarea
                          value={formData.meta_description}
                          onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 h-24"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Current Ranking
                        </label>
                        <input
                          type="text"
                          value={formData.ranking}
                          onChange={(e) => setFormData({ ...formData, ranking: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                          placeholder="e.g., #1, #5, Page 2"
                        />
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Page URL</h3>
                        <a
                          href={page.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 break-all"
                        >
                          {page.url}
                        </a>
                      </div>

                      <div>
                        <h3 className="font-semibold text-gray-900 mb-3">Content Description</h3>
                        <p className="text-gray-700 leading-relaxed">{page.content}</p>
                      </div>

                      {page.meta_description && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Meta Description</h3>
                          <p className="text-gray-700 leading-relaxed">{page.meta_description}</p>
                        </div>
                      )}

                      {page.ranking && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Current Ranking</h3>
                          <span className="bg-gradient-to-r from-green-100 to-green-200 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                            {page.ranking}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* Key Phrases Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiSearch} className="w-5 h-5 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Target Key Phrases & Search Terms</h2>
                  </div>

                  {isEditing ? (
                    <KeyPhrasesInput
                      keyPhrases={formData.keyPhrases}
                      onChange={(phrases) => setFormData({ ...formData, keyPhrases: phrases })}
                      placeholder="Enter key phrase (e.g., best pizza delivery)"
                    />
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {page.keyPhrases?.map((keyPhrase, index) => (
                        <span
                          key={index}
                          className="bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold"
                        >
                          {keyPhrase.phrase}
                        </span>
                      ))}
                      {(!page.keyPhrases || page.keyPhrases.length === 0) && (
                        <p className="text-gray-500 italic">No key phrases added yet</p>
                      )}
                    </div>
                  )}
                </motion.div>
              </>
            )}

            {activeTab === 'social' && (
              <>
                {/* Social Media Content Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiShare2} className="w-5 h-5 text-blue-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">Social Media Content</h2>
                    </div>
                    {isEditing && (
                      <button
                        onClick={generateSocialCaption}
                        className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-2 rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg flex items-center space-x-2"
                      >
                        <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                        <span>Generate Caption</span>
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Social Media Caption
                        </label>
                        <textarea
                          value={formData.social_media_caption}
                          onChange={(e) => setFormData({ ...formData, social_media_caption: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-40"
                          placeholder="Create an engaging caption that promotes this page using the target keywords..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Social Media Image URL
                        </label>
                        <input
                          type="url"
                          value={formData.social_media_image_url}
                          onChange={(e) => setFormData({ ...formData, social_media_image_url: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Recommended Platforms
                        </label>
                        <div className="flex flex-wrap gap-3">
                          {['Facebook', 'Instagram', 'LinkedIn', 'Twitter', 'TikTok'].map((platform) => (
                            <button
                              key={platform}
                              type="button"
                              onClick={() => togglePlatform(platform)}
                              className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-semibold transition-all duration-200 ${
                                formData.social_media_platforms.includes(platform)
                                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <SafeIcon icon={getPlatformIcon(platform)} className="w-4 h-4" />
                              <span>{platform}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Promotion Notes
                        </label>
                        <textarea
                          value={formData.social_media_notes}
                          onChange={(e) => setFormData({ ...formData, social_media_notes: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 h-24"
                          placeholder="Additional notes or instructions for social media promotion..."
                        />
                      </div>

                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="social_media_created"
                          checked={formData.social_media_created}
                          onChange={(e) => setFormData({ ...formData, social_media_created: e.target.checked })}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <label htmlFor="social_media_created" className="font-semibold text-gray-700">
                          Social media content is ready for promotion
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {page.social_media_caption ? (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Ready-Made Caption</h3>
                          <div className="bg-gray-50 rounded-xl p-4">
                            <pre className="text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                              {page.social_media_caption}
                            </pre>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <SafeIcon icon={FiMessageCircle} className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500 italic">No social media content created yet</p>
                        </div>
                      )}

                      {page.social_media_image_url && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Social Media Image</h3>
                          <img
                            src={page.social_media_image_url}
                            alt="Social media preview"
                            className="w-full max-w-md rounded-xl shadow-lg"
                          />
                        </div>
                      )}

                      {page.social_media_platforms && page.social_media_platforms.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Recommended Platforms</h3>
                          <div className="flex flex-wrap gap-3">
                            {page.social_media_platforms.map((platform, index) => (
                              <span
                                key={index}
                                className="flex items-center space-x-2 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold"
                              >
                                <SafeIcon icon={getPlatformIcon(platform)} className="w-4 h-4" />
                                <span>{platform}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {page.social_media_notes && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-3">Promotion Notes</h3>
                          <p className="text-gray-700 leading-relaxed">{page.social_media_notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* Social Media Promotion Guide */}
                {page.social_media_caption && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-8 border border-green-200"
                  >
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-green-900">SEO Boost Strategy</h3>
                    </div>
                    <div className="space-y-4">
                      <p className="text-green-800 text-lg leading-relaxed">
                        🚀 <strong>Maximize your SEO impact!</strong> Share this content on your social media platforms to drive traffic and improve search rankings.
                      </p>
                      <div className="bg-white rounded-xl p-6 border border-green-200">
                        <h4 className="font-bold text-green-900 mb-3">Recommended Action Plan:</h4>
                        <ul className="space-y-2 text-green-800">
                          <li className="flex items-center space-x-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                            <span>Post on {page.social_media_platforms?.join(', ') || 'social media'}</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                            <span>Share with friends and business network</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                            <span>Encourage engagement (likes, comments, shares)</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                            <span>Monitor traffic increase to boost page ranking</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Performance Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Performance Overview</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiTarget} className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">SEO Ranking</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">
                    {page.ranking || 'Not tracked'}
                  </p>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiSearch} className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-purple-800">Key Phrases</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-900">
                    {page.keyPhrases?.length || 0}
                  </p>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiShare2} className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-800">Social Ready</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {page.social_media_created ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <a
                  href={page.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiExternalLink} className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">View Live Page</span>
                </a>
                <button
                  onClick={() => setActiveTab('social')}
                  className="w-full flex items-center space-x-3 p-3 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiShare2} className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Social Media Content</span>
                </button>
                <button
                  onClick={() => navigate(`/admin/website/${websiteId}`)}
                  className="w-full flex items-center space-x-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-800">Back to Website</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageDetail;